<template>
  <div class="team-stats">
    <section v-if="sport === 'Football'" class="match-analysis-summary">
      <p>
        Explore the AI prediction for {{ gameDetails.first_team }} vs {{ gameDetails.second_team }} in
        {{ formattedLeague }}. The current top lean is <strong>{{ topPickLabel }}</strong> at
        <strong>{{ topPickProbability }}</strong> with projected odds of <strong>{{ topPickOdds }}</strong>.
        In the match details below, you can compare the strongest markets, review the model's confidence, inspect the
        home-versus-away team balance across overall strength, attack, defense, and form, and understand where the
        safer view of the fixture sits versus the riskier outcomes.
      </p>
      <div class="summary-links">
        <router-link v-if="leagueRoute" :to="leagueRoute">Back To {{ formattedLeague }}</router-link>
        <router-link to="/football">All Football Predictions</router-link>
      </div>
    </section>
    <h2>Match Analysis</h2>
    <div class="stat-bar" v-for="(stat, index) in stats" :key="index">
      <div class="stat-label-row">
        <span class="team-edge">{{ gameDetails.first_team }}</span>
        <span class="stat-title">{{ stat.title }}</span>
        <span class="team-edge">{{ gameDetails.second_team }}</span>
      </div>

      <div class="bar">
        <div class="bar-section" :style="barStyle(stat.ft)"></div>
        <div class="bar-section" :style="barStyle(stat.st)"></div>
        <div class="bar-percent bar-percent-left">{{ formatPercentage(stat.ft) }}</div>
        <div class="bar-edge-copy">{{ edgeLabel(stat.ft, stat.st) }}</div>
        <div class="bar-percent bar-percent-right">{{ formatPercentage(stat.st) }}</div>
        <div class="center-line"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatLeagueName } from '../utils/formatLeagueName';

export default {
  name: "TeamStatistics",
  props: {
    gameDetails: {
      type: Object,
      required: true
    },
    sport: {
      type: String,
      default: ''
    }
  },
  computed: {
    formattedLeague() {
      return formatLeagueName(this.gameDetails?.league);
    },
    leagueRoute() {
      return this.gameDetails?.league_slug ? `/football/league/${this.gameDetails.league_slug}` : '';
    },
    topPickLabel() {
      return this.gameDetails?.pro_category || 'the leading market';
    },
    topPickProbability() {
      const probability = Number(this.gameDetails?.pro_prob || 0);
      return `${(probability * 100).toFixed(1)}%`;
    },
    topPickOdds() {
      const odds = this.gameDetails?.pro_odds;
      return odds !== undefined && odds !== null ? Number(odds).toFixed(2) : 'N/A';
    },
    stats() {
      return [
        {
          title: 'Overall',
          ft: this.ft_overall,
          st: this.st_overall
        },
        {
          title: 'Attack',
          ft: this.ft_attack,
          st: this.st_attack
        },
        {
          title: 'Defense',
          ft: this.ft_defense,
          st: this.st_defense
        },
        {
          title: 'Form',
          ft: this.ft_form,
          st: this.st_form
        }
      ];
    },
    ft_overall() {
      return this.calculatePercentage(this.gameDetails.ft_avg_points, this.gameDetails.st_avg_points);
    },
    ft_attack() {
      return this.calculatePercentage(this.gameDetails.ft_avg_scored, this.gameDetails.st_avg_scored);
    },
    ft_defense() {
      return this.calculatePercentage(this.gameDetails.st_avg_conced, this.gameDetails.ft_avg_conced);
    },
    ft_form() {
      return this.calculatePercentage(this.gameDetails.ft_form, this.gameDetails.st_form);
    },
    st_overall() {
      return this.calculatePercentage(this.gameDetails.st_avg_points, this.gameDetails.ft_avg_points);
    },
    st_attack() {
      return this.calculatePercentage(this.gameDetails.st_avg_scored, this.gameDetails.ft_avg_scored);
    },
    st_defense() {
      return this.calculatePercentage(this.gameDetails.ft_avg_conced, this.gameDetails.st_avg_conced);
    },
    st_form() {
      return this.calculatePercentage(this.gameDetails.st_form, this.gameDetails.ft_form);
    }
  },
  methods: {
    calculatePercentage(first, second) {
      const total = Number(first || 0) + Number(second || 0);
      if (total <= 0) {
        return 50;
      }
      return (Number(first || 0) / total) * 100;
    },
    barStyle(percentage = 50) {
      const backgroundColor = percentage > 50 ? '#d4edda' : 'white';
      return {
        width: `${percentage}%`,
        backgroundColor,
      };
    },
    formatPercentage(value) {
      return `${Math.round(value)}%`;
    },
    edgeLabel(first, second) {
      const diff = Math.abs(first - second);
      if (diff < 4) {
        return 'Balanced';
      }
      if (first > second) {
        return 'Home Edge';
      }
      return 'Away Edge';
    }
  }
};
</script>

<style scoped>
.team-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  color: #495057;
}

.team-stats h2 {
  margin-bottom: 12px;
}

.match-analysis-summary {
  width: min(65vw, 100%);
  margin: 0 0 10px;
  padding: 16px 18px;
  border: 1px solid #e2eaf2;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.match-analysis-summary p {
  margin: 0;
  color: #5d7083;
  line-height: 1.7;
}

.summary-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.summary-links a {
  padding: 9px 14px;
  border: 1px solid #d7e3ef;
  border-radius: 999px;
  background: #ffffff;
  color: #173252;
  font-weight: 700;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.summary-links a:hover {
  background: #eef5fb;
  border-color: #bfd1e1;
}

.stat-bar {
  width: min(65vw, 100%);
  margin: 16px 0;
}

.stat-label-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
}

.stat-label-row {
  margin-bottom: 8px;
}

.team-edge {
  font-size: 0.8rem;
  font-weight: 700;
  color: #66717d;
}

.team-edge:first-child,
.stat-values span:first-child {
  text-align: left;
}

.team-edge:last-child,
.stat-values span:last-child {
  text-align: right;
}

.stat-title {
  text-align: center;
  font-size: 1rem;
  font-weight: bold;
}

.bar {
  position: relative;
  display: flex;
  height: 3.5vh;
  min-height: 42px;
  width: 100%;
  background-color: #ffffff;
  border: 2px solid #c3e6cb;
  border-radius: 5px;
  overflow: hidden;
}

.bar-section {
  height: 100%;
  transition: width 0.3s ease;
}

.center-line {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  background: rgba(73, 80, 87, 0.12);
  transform: translateX(-50%);
}

.bar-percent {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  font-size: 0.82rem;
  font-weight: 700;
  color: #495057;
  pointer-events: none;
}

.bar-percent-left {
  left: 12px;
}

.bar-percent-right {
  right: 12px;
}

.bar-edge-copy {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  font-size: 0.84rem;
  font-weight: 500;
  color: #495057;
  pointer-events: none;
  text-align: center;
  white-space: nowrap;
}

@media (max-width: 900px) {
  .match-analysis-summary,
  .stat-bar {
    width: 100%;
  }

  .match-analysis-summary {
    padding: 14px 16px;
    border-radius: 18px;
  }

  .team-edge {
    font-size: 0.72rem;
  }

  .stat-title {
    font-size: 0.92rem;
  }

  .bar-percent {
    font-size: 0.76rem;
  }

  .bar-edge-copy {
    font-size: 0.76rem;
  }
}
</style>
